import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './CollectionSelectDrawer.styles';
import { Drawer } from '@/components/Drawer/Drawer';
import { AngleBackIcon, SearchIcon } from '@/components/UI/Icons/Icons';
import { Property, PropertyOption } from '@/api/types/categories.types';
import { Collection } from '@/components/Filters/FiltersDrawer.types';
import { CollectionSelectOption } from '@/components/Filters/CollectionSelectOption';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import {
  SearchInput,
  SearchInputRef,
} from '@/components/SearchInput/SearchInput';
import { NotFound } from '@/components/NotFoundPlaceholder/NotFoundPlaceholder.styles';

type Props = {
  isOpen: boolean;
  toggle: (v?: boolean) => void;
  propertyLegacyId?: string;
  options: PropertyOption[];
  collection?: Collection;
  allProperties?: Property[];
  popularOptions?: PropertyOption[];
  onOptionSelect?: (option: PropertyOption) => void;
};

export const CollectionSelectDrawer: FC<Props> = ({
  isOpen,
  toggle,
  propertyLegacyId,
  options,
  collection,
  allProperties,
  popularOptions,
  onOptionSelect,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<SearchInputRef>(null);

  useEffect(() => {
    searchInputRef.current?.clear();
  }, [isOpen]);

  const name = allProperties?.find(
    el => el.legacyResourceId === propertyLegacyId
  )?.name;

  function handleOptionSelect(value: PropertyOption) {
    if (onOptionSelect) {
      onOptionSelect(value);
    }
  }

  const list = options?.filter(option =>
    option.name.toLowerCase().includes(query.toLowerCase())
  );

  const withPopularOptions = !!popularOptions?.length && !query;

  return (
    <Drawer
      isOpen={isOpen}
      title={name}
      left={{
        icon: <AngleBackIcon />,
        onClick: () => toggle(false),
      }}
      dataTestId={SearchResultsPageTestIds.collectionSelectDrawer}
      hasBottomBorder={false}
      hasBottomMargin={false}
      background={theme.palette.primary.background}
      subtitle={
        <S.Container>
          <SearchInput
            ref={searchInputRef}
            onSearch={setQuery}
            leftIcon={<SearchIcon fill={theme.palette.secondary.labels} />}
            hasPadding={false}
            hasBorderBottom={false}
            customPaddingTop={12}
            customPaddingBottom={12}
          />
        </S.Container>
      }
    >
      {withPopularOptions && (
        <>
          <S.DrawerItemsTitle>{t('common.popular')}</S.DrawerItemsTitle>
          <S.Container>
            {popularOptions.map(option => {
              return (
                <CollectionSelectOption
                  key={option.name}
                  option={option}
                  collection={collection}
                  onOptionSelect={handleOptionSelect}
                />
              );
            })}
          </S.Container>

          <S.DrawerItemsTitle $hasTopGutter>
            {t('common.all')}
          </S.DrawerItemsTitle>
        </>
      )}

      <S.Container>
        {list.length > 0 ? (
          list.map(option => {
            return (
              <CollectionSelectOption
                key={option.name}
                option={option}
                collection={collection}
                onOptionSelect={handleOptionSelect}
              />
            );
          })
        ) : (
          <NotFound>{t('common.nothing_found')}</NotFound>
        )}
      </S.Container>
    </Drawer>
  );
};

CollectionSelectDrawer.displayName = `CollectionSelectDrawer`;
